// =====================================================
// lib/savedAccounts.ts
// Gestion des comptes mémorisés (multi-profils)
// — Ne stocke JAMAIS de mot de passe.
// — Stocke uniquement : id, nom, prénom, avatar (en cache), refresh_token.
// — Le refresh_token permet de régénérer une session sans repasser
//   par le formulaire de connexion (tant qu'il n'a pas été révoqué
//   côté serveur / Supabase).
// =====================================================

import AsyncStorage from '@react-native-async-storage/async-storage';

const STORAGE_KEY = 'harmonia_saved_accounts';
const MAX_ACCOUNTS = 5; // limite raisonnable (comme Netflix : jusqu'à 5 profils)

export interface SavedAccount {
  user_id: string;
  nom: string;
  prenom: string;
  avatar_url: string | null;
  refresh_token: string;
  last_used: number; // Date.now()
}

// ── Lire tous les comptes mémorisés ──────────────────────────────────────────
export async function getSavedAccounts(): Promise<SavedAccount[]> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list: SavedAccount[] = JSON.parse(raw);
    return list.sort((a, b) => b.last_used - a.last_used);
  } catch {
    return [];
  }
}

// ── Ajouter ou mettre à jour un compte mémorisé ──────────────────────────────
export async function saveAccount(account: Omit<SavedAccount, 'last_used'>): Promise<void> {
  try {
    const existing = await getSavedAccounts();
    const filtered = existing.filter(a => a.user_id !== account.user_id);
    const updated: SavedAccount[] = [
      { ...account, last_used: Date.now() },
      ...filtered,
    ].slice(0, MAX_ACCOUNTS);

    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.error('[saveAccount] Erreur sauvegarde:', err);
  }
}

// ── Supprimer un compte précis ────────────────────────────────────────────────
export async function removeSavedAccount(userId: string): Promise<void> {
  try {
    const existing = await getSavedAccounts();
    const updated = existing.filter(a => a.user_id !== userId);
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.error('[removeSavedAccount] Erreur:', err);
  }
}

// ── Tout effacer ───────────────────────────────────────────────────────────────
export async function clearSavedAccounts(): Promise<void> {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.error('[clearSavedAccounts] Erreur:', err);
  }
}

// ── Mettre à jour uniquement le refresh_token d'un compte existant ──────────
// Utile après un refresh réussi (rotation de refresh_token côté Supabase),
// pour que la prochaine reconnexion rapide utilise le token le plus récent.
export async function updateAccountToken(userId: string, newRefreshToken: string): Promise<void> {
  try {
    const existing = await getSavedAccounts();
    const updated = existing.map(a =>
      a.user_id === userId
        ? { ...a, refresh_token: newRefreshToken, last_used: Date.now() }
        : a
    );
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.error('[updateAccountToken] Erreur:', err);
  }
}
